/**
 * Admin › Integrações › **Mapas e rotas** (W8-GEO-REAL, D436, ADR-0068; owner answer D435 §5.0 items 1-2) — the pure
 * view-model of the geo vendor slots.
 *
 * Four vendors the governed cascade can name: two with an API key (write-only — the console shows a masked hint and a
 * configured badge, never the key; a blank key keeps the stored one) and two self-hosted (a base URL; the address search
 * one also an identifying User-Agent). Each slot has an on/off switch and the cost of one billable call in minor units
 * WITH its ISO 4217 currency (the usage log prices every call with it). The vendor ORDER, the precision and the guard are
 * the governed `geo.providerCascade` rule (Settings › governed rules), never this screen.
 *
 * Neutral: no country, currency, URL or key is baked in; vendor ids are the server's.
 */
import { type Result, ok, err } from '@vello/core';
import {
  type ApiError,
  type GeoRouterEndpointHealth,
  type GeoRouterHealthView,
  type GeoUsageView,
  type GeoVendor,
  type GeoVendorSlotView,
  type IntegrationsApi,
  type SetGeoVendorSlotInput,
} from '@vello/api-client';

export const GEO_VENDORS: readonly GeoVendor[] = ['here', 'google', 'nominatim', 'osrm'];

/** A slot as the operator types it. `apiKey` is write-only (blank keeps the stored key). */
export interface GeoSlotForm {
  readonly enabled: boolean;
  readonly apiKey: string;
  readonly baseUrl: string;
  /** D847: the motorcycle router's URL (osrm only); blank = none (motorcycles use the car graph). */
  readonly motorcycleBaseUrl: string;
  readonly userAgent: string;
  /** Minor units as typed (digits only); blank = no cost recorded. */
  readonly costPerRequestMinor: string;
  readonly costCurrency: string;
}

export type GeoSlotField = 'apiKey' | 'baseUrl' | 'motorcycleBaseUrl' | 'userAgent' | 'cost';
export type GeoSlotIssueCode = 'KEY_REQUIRED' | 'KEY_MALFORMED' | 'URL_REQUIRED' | 'URL_INVALID' | 'MOTORCYCLE_URL_INVALID' | 'USER_AGENT_TOO_LONG' | 'COST_INVALID' | 'CURRENCY_REQUIRED' | 'CURRENCY_INVALID';
export interface GeoSlotIssue {
  readonly field: GeoSlotField;
  readonly code: GeoSlotIssueCode;
}

export function emptyGeoSlotForm(): GeoSlotForm {
  return { enabled: false, apiKey: '', baseUrl: '', motorcycleBaseUrl: '', userAgent: '', costPerRequestMinor: '', costCurrency: '' };
}

/** Seed the form from the server view (the key is never sent back, so `apiKey` stays blank). */
export function geoSlotFormFromView(view: GeoVendorSlotView): GeoSlotForm {
  return {
    enabled: view.enabled,
    apiKey: '',
    baseUrl: view.baseUrl ?? '',
    motorcycleBaseUrl: view.motorcycleBaseUrl ?? '',
    userAgent: view.userAgent ?? '',
    costPerRequestMinor: view.costPerRequestMinor !== undefined ? String(view.costPerRequestMinor) : '',
    costCurrency: view.costCurrency ?? '',
  };
}

function validHttpUrl(v: string): boolean {
  try {
    const u = new URL(v);
    return (u.protocol === 'http:' || u.protocol === 'https:') && u.username === '' && u.password === '' && u.search === '' && u.hash === '';
  } catch {
    return false;
  }
}

/**
 * Validate + normalize a slot into the wire input, or every issue at once. `view` is the stored slot (a stored key or URL
 * satisfies "required" when the operator leaves the field blank). The server re-validates everything.
 */
export function normalizeGeoSlotForm(view: GeoVendorSlotView, form: GeoSlotForm): Result<SetGeoVendorSlotInput, readonly GeoSlotIssue[]> {
  const issues: GeoSlotIssue[] = [];
  const apiKey = form.apiKey.trim();
  const baseUrl = form.baseUrl.trim();
  const motorcycleBaseUrl = form.motorcycleBaseUrl.trim();
  const userAgent = form.userAgent.trim();
  const costRaw = form.costPerRequestMinor.trim();
  const currency = form.costCurrency.trim().toUpperCase();
  if (view.requires === 'apiKey') {
    if (apiKey !== '' && (apiKey.length < 8 || /\s/.test(apiKey))) issues.push({ field: 'apiKey', code: 'KEY_MALFORMED' });
    if (form.enabled && apiKey === '' && !view.configured) issues.push({ field: 'apiKey', code: 'KEY_REQUIRED' });
  } else {
    if (baseUrl !== '' && !validHttpUrl(baseUrl)) issues.push({ field: 'baseUrl', code: 'URL_INVALID' });
    if (form.enabled && baseUrl === '' && view.baseUrl === undefined) issues.push({ field: 'baseUrl', code: 'URL_REQUIRED' });
  }
  if (view.vendor === 'osrm' && motorcycleBaseUrl !== '' && !validHttpUrl(motorcycleBaseUrl)) issues.push({ field: 'motorcycleBaseUrl', code: 'MOTORCYCLE_URL_INVALID' });
  if (userAgent.length > 200) issues.push({ field: 'userAgent', code: 'USER_AGENT_TOO_LONG' });
  let cost: number | undefined;
  if (costRaw !== '') {
    if (!/^\d{1,7}$/.test(costRaw)) issues.push({ field: 'cost', code: 'COST_INVALID' });
    else cost = Number(costRaw);
  }
  if (currency !== '' && !/^[A-Z]{3}$/.test(currency)) issues.push({ field: 'cost', code: 'CURRENCY_INVALID' });
  if (cost !== undefined && cost > 0 && currency === '') issues.push({ field: 'cost', code: 'CURRENCY_REQUIRED' });
  if (issues.length > 0) return err(issues);
  return ok({
    enabled: form.enabled,
    ...(view.requires === 'apiKey' && apiKey !== '' ? { apiKey } : {}),
    ...(view.requires === 'baseUrl' && baseUrl !== '' ? { baseUrl } : {}),
    // D847: a typed URL sets the motorcycle router; blanking a stored one clears it (`null`); blank with none stored sends nothing.
    ...(view.vendor === 'osrm' ? (motorcycleBaseUrl !== '' ? { motorcycleBaseUrl } : view.motorcycleBaseUrl !== undefined ? { motorcycleBaseUrl: null } : {}) : {}),
    ...(view.vendor === 'nominatim' && userAgent !== '' ? { userAgent } : {}),
    ...(cost !== undefined ? { costPerRequestMinor: cost } : {}),
    ...(currency !== '' ? { costCurrency: currency } : {}),
  });
}

/** The console message of each form issue. */
export const GEO_SLOT_ISSUE_MESSAGE: Readonly<Record<GeoSlotIssueCode, string>> = {
  KEY_REQUIRED: 'geoint.err.keyRequired',
  KEY_MALFORMED: 'geoint.err.keyMalformed',
  URL_REQUIRED: 'geoint.err.urlRequired',
  URL_INVALID: 'geoint.err.urlInvalid',
  MOTORCYCLE_URL_INVALID: 'geoint.err.motorcycleUrlInvalid',
  USER_AGENT_TOO_LONG: 'geoint.err.userAgent',
  COST_INVALID: 'geoint.err.costInvalid',
  CURRENCY_REQUIRED: 'geoint.err.currencyRequired',
  CURRENCY_INVALID: 'geoint.err.currencyInvalid',
};

/** The router probe as a tone + message id (+ the latency or the reason). */
export function routerHealthState(h: GeoRouterEndpointHealth): { readonly tone: 'success' | 'danger' | 'warning'; readonly messageId: string; readonly detail?: string } {
  if (h.status === 'UP') return { tone: 'success', messageId: 'geoint.health.up', detail: String(h.latencyMs) };
  if (h.status === 'DOWN') return { tone: 'danger', messageId: 'geoint.health.down', detail: h.reason };
  return { tone: 'warning', messageId: 'geoint.health.unconfigured' };
}

/**
 * D847: the probe of the car graph and, when a motorcycle router is registered, of the motorcycle one - each as a tone + message id (+ detail). With no
 * motorcycle router the second line is absent and the screen says motorcycles use the car graph.
 */
export function routerHealthLines(h: GeoRouterHealthView): { readonly car: ReturnType<typeof routerHealthState>; readonly motorcycle?: ReturnType<typeof routerHealthState> } {
  return { car: routerHealthState(h), ...(h.motorcycle !== undefined ? { motorcycle: routerHealthState(h.motorcycle) } : {}) };
}

/** Usage rows summed per provider for the console (cost per currency; a row with no currency costs nothing). */
/** D854 (L7a-013): one provider's rollup over the window; `failureReasons` says WHY its calls failed (stable `GeoReason`s, most frequent first). */
export interface GeoProviderUsage {
  readonly providerId: string;
  readonly calls: number;
  readonly cachedHits: number;
  readonly failures: number;
  readonly costs: readonly { readonly amountMinor: bigint; readonly currency: string }[];
  readonly failureReasons: readonly { readonly reason: string; readonly count: number }[];
}

/**
 * The share of a provider's vendor calls (cache hits excluded - they never left) that failed, in whole percent rounded down; `undefined` when none left.
 * Display only: the readiness page owns the threshold.
 */
export function failurePercent(u: Pick<GeoProviderUsage, 'calls' | 'cachedHits' | 'failures'>): number | undefined {
  const attempts = u.calls - u.cachedHits;
  return attempts > 0 ? Math.min(100, Math.floor((u.failures * 100) / attempts)) : undefined;
}

export function usageByProvider(view: GeoUsageView): readonly GeoProviderUsage[] {
  const byProvider = new Map<string, { calls: number; cachedHits: number; failures: number; costs: Map<string, bigint>; reasons: Map<string, number> }>();
  const slot = (providerId: string) => {
    const cur = byProvider.get(providerId) ?? { calls: 0, cachedHits: 0, failures: 0, costs: new Map<string, bigint>(), reasons: new Map<string, number>() };
    byProvider.set(providerId, cur);
    return cur;
  };
  for (const r of view.rows) {
    const cur = slot(r.providerId);
    cur.calls += r.calls;
    cur.cachedHits += r.cachedHits;
    cur.failures += r.failures;
    if (r.currency !== null && BigInt(r.costMinor) > 0n) cur.costs.set(r.currency, (cur.costs.get(r.currency) ?? 0n) + BigInt(r.costMinor));
  }
  for (const f of view.failures ?? []) {
    if (!byProvider.has(f.providerId)) continue;
    const cur = slot(f.providerId);
    cur.reasons.set(f.reason, (cur.reasons.get(f.reason) ?? 0) + f.count);
  }
  return [...byProvider.entries()]
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([providerId, v]) => ({
      providerId,
      calls: v.calls,
      cachedHits: v.cachedHits,
      failures: v.failures,
      costs: [...v.costs.entries()].map(([currency, amountMinor]) => ({ amountMinor, currency })),
      failureReasons: [...v.reasons.entries()].map(([reason, count]) => ({ reason, count })).sort((x, y) => y.count - x.count || (x.reason < y.reason ? -1 : 1)),
    }));
}

interface GeoIntegrationsRuntime {
  readonly integrations: IntegrationsApi;
}

export function getGeoProviders(runtime: GeoIntegrationsRuntime): Promise<Result<{ readonly providers: readonly GeoVendorSlotView[] }, ApiError>> {
  return runtime.integrations.getGeoProviders();
}

export function setGeoProvider(runtime: GeoIntegrationsRuntime, vendor: GeoVendor, input: SetGeoVendorSlotInput): Promise<Result<GeoVendorSlotView, ApiError>> {
  return runtime.integrations.setGeoProvider(vendor, input);
}

export function probeGeoRouter(runtime: GeoIntegrationsRuntime, input: { readonly marketId?: string; readonly point?: { readonly latitude: number; readonly longitude: number } }): Promise<Result<GeoRouterHealthView, ApiError>> {
  return runtime.integrations.probeGeoRouter(input);
}

export function getGeoUsage(runtime: GeoIntegrationsRuntime, input: { readonly since?: string; readonly marketId?: string } = {}): Promise<Result<GeoUsageView, ApiError>> {
  return runtime.integrations.getGeoUsage(input);
}
